<template>
  <div class="matching">
    <t-content main>
      <c-loading-indicator :show="loadingIndicator" data-qe-id="loader-spinning"></c-loading-indicator>

      <t-container>
        <div class="top">
          <div class="loading-message" v-html="headline"></div>
          <t-button
            v-if="matchingProgress === matchingState.LOADING"
            class="exit-matching-button"
            m
            @click="onCancel"
            data-qe-id="cancel-button"
          >
            {{ $t('matchingView.button.cancel') }}
          </t-button>
        </div>

        <div class="matching-layout">
          <c-matching-slot
            :name="store.state.user.name"
            :user-avatar-url="avatarUrl"
            :color="colorSchema"
            :state="matchingState.SUCCESS"
          />

          <c-horizontal-progress
            color="midGreen"
            size="L"
            :class="['loading-progress', { hidden: matchingProgress !== matchingState.LOADING }]"
          />

          <c-matching-slot
            :color="colorSchema"
            v-for="index in numberOfSlotsExclOwn"
            :key="index"
            :state="matchingProgress"
          />
        </div>
      </t-container>
    </t-content>
    <t-container>
      <c-matching-info />
    </t-container>
    <c-error-modal v-if="showErrorModal" @close="closeErrorModal">
      <div v-html="md($t('matchingView.error.' + matchingErrorI18nKey))"></div>
    </c-error-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, Ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'

import { marked } from 'marked'
import { ACTION } from '@gd/tracking'
import CMatchingSlot from '@/components/CMatchingSlot.vue'
import CMatchingInfo from '@/components/CMatchingInfo.vue'
import CHorizontalProgress from '@/components/CHorizontalProgress.vue'
import CErrorModal from '@/components/CErrorModal.vue'
import { TournamentTypeToName } from '@/model/TournamentType'
import { MatchingProgress } from '@/model/Matching'
import { setTimeout } from 'timers'
import { getTournamentType } from '@/store/lib/util'
import { GD } from '@gd/storage'
import { ZoneIdColorSchemeMap } from '@/model/ColorScheme'
import { ERROR_CODES } from '@/model/ErrorCodes'
import matchingSuccessSound from '@/lib/matchingSuccessSound'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'

const props = defineProps<{ tId: string; zoneId: string }>()
const router = useRouter()
const store = useStore()
const { t } = useI18n()

/**
 * The matching module, actually just a large pending screen (user-facing part) but is wired
 * to MatchingWebsocketClient to initiate matching and then forward user to the game if
 * matching is successful.
 * tId property must be set for this module to work.
 */
const matchingState = MatchingProgress
const matchingProgress: Ref<MatchingProgress> = ref(MatchingProgress.LOADING)
const loadingIndicator = ref(true)
const showErrorModal = ref(false)

const storageGD: GD = store.getters.storageGD
const failReason = ref('') // unknown_error, server_sent_leave, server_sent_close

const headline = computed(() => {
  return marked(t('matchingView.main.headline'))
})

const avatarUrl = computed(() => {
  return store.state.user.avatarUrl
})

const colorSchema = computed(() => {
  return ZoneIdColorSchemeMap[props.zoneId]
})

const numberOfSlotsExclOwn = computed((): number => {
  return store.getters['matching/matchingData'].numberOfPlayers - 1
})

function closeErrorModal() {
  router.push(routeBackToOverview.value)
}

function md(s: string) {
  return marked(s)
}

const matchingErrorI18nKey = computed((): string => {
  switch (failReason.value) {
    case 'server_sent_closed':
      return 'server_sent_closed'
    default:
      return 'unknown_error'
  }
})

const routeBackToOverview = computed(() => {
  const tournamentType = getTournamentType(
    store.getters['matching/matchingData'].gametype,
    store.getters['matching/matchingData'].zoneId
  )
  const tournamentName = TournamentTypeToName[tournamentType]
  return '/tournaments/' + tournamentName
})

onMounted(() => {
  window.addEventListener('unload', handleUnload)
  showErrorModal.value = false
  loadingIndicator.value = true
  failReason.value = ''
  store
    .dispatch('matching/fetchMatching', props.tId)
    .then(() => {
      loadingIndicator.value = false
      store
        .dispatch('matching/startMatching')
        .then((startGameParams: any) => {
          track({
            key: 'matching',
            action: ACTION.startGame,
            gameType: store.getters['matching/matchingData'].gametype,
            tournamentId: store.getters['matching/matchingData'].tournamentId,
          })
          return matchingSucceeded(startGameParams)
        })
        .catch(matchingCancelled)
    })
    .catch((err: any) => {
      jslog.send({ error: err })
      if (err && err.errorCode === 401) {
        router.replace('/extern/login')
      } else {
        router.push(`/error/${(err && err.errorCode) || ERROR_CODES.UNKNOWN}/${props.zoneId}`)
      }
    })
  // thats only for testing to simulate a broken ws connection
  if (process.env.NODE_ENV !== 'production') {
    ;(window as any).GD.$toast.error(
      "<div class='t-text'>" +
        '<p><strong>DEVMODE</strong></p>' +
        '<p>Simulate matching errors</p>' +
        '<p style="display: flex;flex-direction: column;">' +
        '<button style="margin-bottom: .4em;">unknown_error</button>' +
        '<button style="margin-bottom: .4em;">server_sent_leave</button>' +
        '<button style="margin-bottom: .4em;">server_sent_closed</button>' +
        '</p>' +
        '</div>',
      {
        icon: 'icf-specialAutumn',
        onContent: (e: any) => {
          // detect that the button is clicked
          if (e.target && e.target.tagName === 'BUTTON') {
            store.dispatch('matching/exitMatching')
            matchingCancelled(e.target.textContent)
          } else {
            // stop propagation will prevent toast from self closing
            e.stopPropagation()
          }
        },
      }
    )
  }
})

function onCancel() {
  track({
    key: 'matching',
    gameType: store.getters['matching/matchingData'].gametype,
    tournamentId: store.getters['matching/matchingData'].tournamentId,
    action: ACTION.cancelMatching,
  })
  exitMatching()
}

onUnmounted(() => {
  exitMatching(false)
})

function handleUnload(e: Event) {
  exitMatching(false)
}

function exitMatching(navigateBack = true) {
  window.removeEventListener('unload', handleUnload)
  store
    .dispatch('matching/exitMatching')
    .then(() => {
      if (navigateBack) {
        router.push(routeBackToOverview.value)
      }
    })
    .catch((error) => {
      jslog.send({ error: error })
    })
}

function matchingCancelled(reason: string) {
  failReason.value = reason
  showErrorModal.value = true
  jslog.send({ name: 'tournaments-matching-cancelled', data: { reason, tid: props.tId } })
}

function matchingSucceeded(startGameParams: any) {
  matchingProgress.value = MatchingProgress.SUCCESS
  matchingSuccessSound.play()
  // before proceeding, wait a random seconds duration: if all joined/matched users would proceed in parallel,
  // there's a high chance of race conditions if N users are requesting to create the competition at the same time
  const waitUntilProceeding = (Math.floor(Math.random() * 3) + 1) * 1000
  setTimeout(() => {
    proceedToGame(startGameParams)
  }, waitUntilProceeding)
}

function proceedToGame({ config, digest }: { config: string; digest: string }) {
  // the play-game page expects to be navigated to with a POST. doing the POST with a fetch will not work because
  // that would not change the page and changing document.location afterwards won't work, too
  // therefore, we'll take a slightly crude approach: create a FORM element on the fly, instrument it and submit
  const proceedToGameForm = document.createElement('form')
  proceedToGameForm.setAttribute('method', 'POST')
  // TODO the game page url should not be hard-wired! should be passed in - as part of matchingData?
  proceedToGameForm.setAttribute('action', '/gd/tournaments/play.xhtml')

  proceedToGameForm.appendChild(toHiddenParamElement('config', config))
  proceedToGameForm.appendChild(toHiddenParamElement('digest', digest))

  proceedToGameForm.appendChild(
    toHiddenParamElement('canPlayHtml', (storageGD.supportedTechs.indexOf('html5_webgl') !== -1).toString())
  )
  /**
   * TODO can be removed ?
   */
  proceedToGameForm.appendChild(toHiddenParamElement('canPlayFlash', 'false'))

  proceedToGameForm.appendChild(toHiddenParamElement('newTournaments', 'true'))

  const formParent = document.querySelector('.matching')
  formParent.appendChild(proceedToGameForm)
  proceedToGameForm.submit()
}

function toHiddenParamElement(name: string, value: string): HTMLInputElement {
  const hiddenParamElement = document.createElement('input')
  hiddenParamElement.setAttribute('type', 'hidden')
  hiddenParamElement.setAttribute('name', name)
  hiddenParamElement.setAttribute('value', value)
  return hiddenParamElement
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
.loading-progress {
  display: inline-block;
  position: relative;
  bottom: 1.75em;
  margin: 0 35px;
  opacity: 1;
  transition: opacity 750ms;
  &.hidden {
    opacity: 0;
  }
}
.top {
  display: flex;
  position: relative;
  width: 100%;
  min-height: 8.125em;
  justify-content: space-evenly;
  align-items: center;
  padding: 2.8125rem 0;
  .loading-message {
    font-size: 1.125em;
    text-align: center;
  }
  .exit-matching-button.exit-matching-button {
    position: absolute;
    right: 0;
  }
}
.matching-layout {
  display: block;
  text-align: center;
  padding-bottom: 6em;
}
</style>
